import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../../contexts/AuthContext';

export const Navbar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Pricing', path: '/plans' },
    { label: 'FAQ', path: '/faq' },
    ...(currentUser ? [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Analyze', path: '/analyze' }
    ] : [])
  ];

  const authButtons = currentUser ? (
    <>
      <Typography variant="body1" sx={{ mr: 2 }}>
        {currentUser.email}
      </Typography>
      <Button color="inherit" onClick={logout}>
        Logout
      </Button>
    </>
  ) : (
    <>
      <Button
        color="inherit"
        component={RouterLink}
        to="/login"
        sx={{ mr: 1 }}
      >
        Login
      </Button>
      <Button
        variant="contained"
        color="secondary"
        component={RouterLink}
        to="/plans"
      >
        Get Started
      </Button>
    </>
  );

  return (
    <AppBar position="sticky">
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              mr: 2,
            }}
          >
            <Box
              component="img"
              src="/images/logo-new.png"
              alt="GovToolsPro Logo"
              sx={{
                height: 36,
                width: 'auto',
                mr: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
              }}
            >
              GovToolsPro
            </Typography>
          </Box>

          {isMobile ? (
            <>
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenu}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {navItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    onClick={handleClose}
                    selected={isActive(item.path)}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                {currentUser ? (
                  <MenuItem onClick={logout}>Logout</MenuItem>
                ) : (
                  <>
                    <MenuItem
                      component={RouterLink}
                      to="/login"
                      onClick={handleClose}
                    >
                      Login
                    </MenuItem>
                    <MenuItem
                      component={RouterLink}
                      to="/plans"
                      onClick={handleClose}
                    >
                      Get Started
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    color="inherit"
                    size="small"
                    sx={{
                      borderBottom: isActive(item.path)
                        ? '2px solid white'
                        : '2px solid transparent',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>{authButtons}</Box>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};
